/**
 * @file SystemPopulationGroupList.js
 * @description Displays a list of system population groups with server-side pagination and client-side sorting.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import style from './PopulationGroup.module.scss';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { searchPopulationGroups } from '../../services/populationGroupService';
import PaginationComponent from '../helpers/PaginationComponent';
import styles from './PopulationGroup.module.scss';
import SearchComponent from '../helpers/search/SearchComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PopulationGroupDownloadMenu from './PopulationGroupDownloadMenu';
import { getText } from '../../i18n';

const SystemPopulationGroupList = ({ onSelectGroup, onViewIndividuals }) => {
    const SortDirections = {
        ASCENDING: 'asc',
        DESCENDING: 'desc',
    };

    const ITEMS_PER_PAGE = 20;

    const [systemGroups, setSystemGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    useEffect(() => {
        loadSystemGroups();
    }, [currentPage]);

    useEffect(() => {
        if (!initialLoadComplete) return;

        const timeoutId = setTimeout(() => {
            loadSystemGroups();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, sortConfig]);

    const loadSystemGroups = async () => {
        try {
            setIsLoading(true);
            const response = await searchPopulationGroups(
                null,
                currentPage,
                ITEMS_PER_PAGE,
                false,
                true,
                false,
                searchTerm,
                sortConfig.key + sortConfig.direction,
            );
            setSystemGroups(response.populationGroups);
            setTotalPages(Math.ceil(response.recordCount / ITEMS_PER_PAGE));
            setInitialLoadComplete(true);
        } catch (error) {
            console.error('Error fetching system groups:', error);
            Toast.error(getText('msg_5aced64674f7'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = key => {
        setSortConfig({
            key,
            direction:
                sortConfig.key === key &&
                sortConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        });
    };

    const handleGroupClick = group => {
        onSelectGroup(group.id);
    };

    const renderSortArrow = key => {
        if (sortConfig.key !== key) {
            return null;
        }
        return (
            <span className={`px-2 ${style.sortArrowIcon}`}>
                <svg
                    width="11"
                    height="6"
                    viewBox="0 0 11 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d={
                            sortConfig.direction === SortDirections.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const handleSearch = term => {
        setSearchTerm(term);
    };

    return (
        <>
            <div className="table-responsive-xxl col-12 col-xs-12  m-auto">
                <div className="pb-4">
                    <div className="ms-2">
                        <SearchComponent
                            onSearch={event => handleSearch(event)}
                            placeholder={getText('msg_f90a80f7e40f')}
                            initialValue={searchTerm}
                        />
                    </div>
                </div>
                <table className="table table-hover align-middle">
                    <thead className="custom-table-header table-light">
                        <tr>
                            <th
                                className={style.columnHead}
                                onClick={() => handleSort('name')}
                            >
                                {getText('msg_709a23220f2c')}{' '}
                                {renderSortArrow('name')}
                            </th>
                            <th
                                className={`${style.columnHead} ${style.actionsColumn}`}
                            >
                                {getText('msg_c3cd636a585b')}
                            </th>
                        </tr>
                    </thead>
                    {!isLoading && systemGroups.length === 0 && (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            title={getText('msg_aca14ec90fb5')}
                            description={getText('msg_12987c465e21')}
                        />
                    )}
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td className="text-center" colSpan={2}>
                                    <Spinner
                                        width={50}
                                        className={styles.loadingSpinner}
                                    />
                                </td>
                            </tr>
                        ) : (
                            systemGroups.map(group => (
                                <tr key={group.id}>
                                    <td
                                        className="item-page-name titleCell"
                                        onClick={() => handleGroupClick(group)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={e => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                handleGroupClick(group);
                                            }
                                        }}
                                    >
                                        {group.name}
                                    </td>
                                    <td className={style.actionsColumn}>
                                        <div className={styles.actionsCell}>
                                            <PopulationGroupDownloadMenu
                                                populationGroup={group}
                                            />
                                            <span
                                                className={styles.viewButton}
                                                onClick={() => {
                                                    onViewIndividuals(group);
                                                }}
                                            >
                                                <i className="bi bi-eye"></i>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {systemGroups.length > 0 && (
                <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}
        </>
    );
};

SystemPopulationGroupList.propTypes = {
    onSelectGroup: PropTypes.func.isRequired,
};

export default SystemPopulationGroupList;
